/**
 * Framework-free replacement for `next-typesafe-url`'s `$path` (WS5, rule 07).
 *
 * Interpolates `[param]` tokens in a route template from `routeParams` and
 * appends `searchParams` as a query string. Serialization matches `$path`
 * exactly (verified by the golden test `appPath.golden.unit.test.ts`):
 * both route-param values and search-param values are `encodeURIComponent`-
 * encoded (space → `%20`, not `+`), and the `?` is omitted when there are no
 * search params.
 *
 * The route table (`config/router.ts`) is tenant-owned, so this needs no
 * generated types — the tenant passes the same `{ route, routeParams?,
 * searchParams? }` shape it passed to `$path`.
 *
 * Values are `string | number | null | undefined` to accept the same
 * (sometimes optional) tenant fields `$path` accepted. Coercion via `String`
 * matches `$path` for the in-use surface: `null` → `"null"`, numbers →
 * their decimal string, strings verbatim. (`$path` throws on a genuinely
 * *absent* dynamic segment; every in-use route param is present, so appPath
 * simply serializes whatever is passed.)
 */
export type AppPathValue = string | number | null | undefined;

export type AppPathArgs = {
  readonly route: string;
  readonly routeParams?: Record<string, AppPathValue>;
  readonly searchParams?: Record<string, AppPathValue>;
};

export const appPath = ({ route, routeParams, searchParams }: AppPathArgs): string => {
  let path = route;

  if (routeParams !== undefined) {
    for (const [key, value] of Object.entries(routeParams)) {
      path = path.replace(`[${key}]`, encodeURIComponent(String(value)));
    }
  }

  const query =
    searchParams === undefined
      ? ''
      : Object.entries(searchParams)
          .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
          .join('&');

  return query === '' ? path : `${path}?${query}`;
};
